import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import { Schema } from '@mcn-platform/models';

export type SectionSearchEntry = {
  path: string;
  tabKey: string;
  sectionTitle: string;
  labels: string[];
};

function safePropertyLabel(modelName: string, fieldPath: string): string | null {
  try {
    const p = Schema.getProperty(modelName, fieldPath) as {
      label?: string;
      title?: string;
    };
    return String(p.label || p.title || fieldPath);
  } catch {
    return null;
  }
}

function collectFieldLabels(modelName: string, elements: unknown[]): string[] {
  const labels: string[] = [];
  for (const el of elements) {
    if (typeof el === 'string') {
      const lab = safePropertyLabel(modelName, el);
      if (lab) labels.push(lab);
    }
  }
  return labels;
}

/**
 * Walks custom ItemEdit form config and collects subsection/tab labels for search.
 */
export function collectSectionSearchIndex(
  formConfig: unknown[] | undefined,
  modelName: string,
): SectionSearchEntry[] {
  const out: SectionSearchEntry[] = [];
  if (!formConfig || !Array.isArray(formConfig)) return out;

  const walk = (element: any, tabKey: string, pathPrefix: string) => {
    if (!element || typeof element === 'string') return;
    if (element.type === 'tabs') {
      element.elements?.forEach((tab: any) => {
        const key = tab.name;
        out.push({
          path: `__tab__${key}`,
          tabKey: key,
          sectionTitle: String(tab.label || key),
          labels: [String(tab.label || key)],
        });
        walk(tab, key, key);
      });
      return;
    }
    if (element.type === 'group') {
      const nextPrefix = pathPrefix || element.name || '';
      element.elements?.forEach((el: any) => walk(el, tabKey || element.name, nextPrefix));
      return;
    }
    if (element.type === 'subGroup') {
      const path = pathPrefix ? `${pathPrefix}.${element.name}` : element.name;
      const fieldLabels = collectFieldLabels(modelName, element.elements || []);
      const componentLabels = (element.elements || [])
        .filter((el: { type?: string; label?: string; name?: string }) => el?.type === 'component')
        .map((el: { label?: string; name?: string }) => String(el.label || el.name || ''))
        .filter(Boolean);
      const labels = [String(element.label || element.name), ...fieldLabels, ...componentLabels];
      out.push({
        path,
        tabKey,
        sectionTitle: String(element.label || element.name),
        labels,
      });
      element.elements?.forEach((el: any) => walk(el, tabKey, path));
      return;
    }
    if (element.type === 'component') {
      const path = pathPrefix ? `${pathPrefix}.${element.name}` : element.name;
      out.push({
        path,
        tabKey,
        sectionTitle: String(element.label || element.name),
        labels: [String(element.label || element.name)],
      });
      return;
    }
    if (element.type === 'twoColumn') {
      const colPrefix = `${pathPrefix}.${element.name || 'twoColumn'}`;
      element.elements?.forEach((el: any) => walk(el, tabKey, colPrefix));
    }
  };

  formConfig.forEach((root) => walk(root, '', ''));
  return out;
}

export function sectionPathToDomId(sectionPath: string): string {
  return `edit-section-${sectionPath.replace(/\./g, '__')}`;
}

function countLabelsMatchingQuery(labels: string[], queryLower: string): number {
  if (!queryLower) return 0;
  return labels.reduce(
    (n, label) => (label.toLowerCase().includes(queryLower) ? n + 1 : n),
    0,
  );
}

/** First URL/tab segment for a subsection path (e.g. `general.integration` → `general`). */
export function sectionPathRootTabKey(path: string): string {
  if (path.startsWith('__tab__')) {
    return path.slice('__tab__'.length);
  }
  const dot = path.indexOf('.');
  return dot === -1 ? path : path.slice(0, dot);
}

type SectionCollapseContextValue = {
  searchQuery: string;
  setSearchQuery: (q: string) => void;
  searchIndex: SectionSearchEntry[];
  /** Paths that match the current search (sections stay expanded while searching). */
  matchingPaths: Set<string>;
  /** Whether a section should render expanded (accordion). */
  isSectionExpanded: (path: string) => boolean;
  onSectionToggle: (path: string, expanded: boolean) => void;
  focusSectionPath: (path: string) => void;
  expandAllSections: () => void;
  collapseAllSections: () => void;
  tabsUrlParam: string | null;
  /** When set, only subsection paths under these tab keys use collapse UI; others stay always open. */
  isCollapsiblePath: (path: string) => boolean;
};

const SectionCollapseContext = createContext<SectionCollapseContextValue | null>(null);

export function useSectionCollapse(): SectionCollapseContextValue | null {
  return useContext(SectionCollapseContext);
}

type ProviderProps = {
  children: React.ReactNode;
  modelName: string;
  formConfig: unknown[];
  tabsUrlParam: string | null;
  /**
   * When non-empty, only subGroups under these tab `name`s (e.g. `general`, `tools`) get collapse controls.
   * When omitted or empty, all tabs use collapse UI (backward compatible).
   */
  collapsibleTabKeys?: string[];
};

export function ItemEditSectionCollapseProvider({
  children,
  modelName,
  formConfig,
  tabsUrlParam,
  collapsibleTabKeys,
}: ProviderProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState('');
  const [manualOpen, setManualOpen] = useState<Record<string, boolean>>({});
  const [dismissedWhileSearching, setDismissedWhileSearching] = useState<Set<string>>(
    () => new Set(),
  );

  const searchIndex = useMemo(
    () => collectSectionSearchIndex(formConfig, modelName),
    [formConfig, modelName],
  );

  const firstTabKey = useMemo(() => {
    const root = formConfig[0] as { type?: string; elements?: Array<{ name?: string }> } | undefined;
    if (!root || root.type !== 'tabs' || !root.elements?.length) return '';
    return root.elements[0].name ?? '';
  }, [formConfig]);

  const activeTabKey = tabsUrlParam
    ? (searchParams.get(tabsUrlParam) || firstTabKey)
    : '';

  const collapsibleTabKeysSet = useMemo(() => {
    if (!collapsibleTabKeys?.length) return null;
    return new Set(collapsibleTabKeys);
  }, [collapsibleTabKeys]);

  const isCollapsiblePath = useCallback(
    (path: string) => {
      if (path.startsWith('__tab__')) return false;
      if (!collapsibleTabKeysSet) return true;
      return collapsibleTabKeysSet.has(sectionPathRootTabKey(path));
    },
    [collapsibleTabKeysSet],
  );

  const q = searchQuery.trim().toLowerCase();

  useEffect(() => {
    setDismissedWhileSearching(new Set());
  }, [q]);

  const matchingPaths = useMemo(() => {
    if (!q) return new Set<string>();
    const next = new Set<string>();
    for (const e of searchIndex) {
      if (e.labels.some((label) => label.toLowerCase().includes(q))) {
        next.add(e.path);
      }
    }
    return next;
  }, [q, searchIndex]);

  const hasMatchesOnActiveTab = useMemo(() => {
    if (!q || !activeTabKey) return false;
    return searchIndex.some(
      (e) => e.tabKey === activeTabKey && matchingPaths.has(e.path),
    );
  }, [q, activeTabKey, searchIndex, matchingPaths]);

  const isSectionExpanded = useCallback(
    (path: string) => {
      const pathTab = sectionPathRootTabKey(path);
      if (!isCollapsiblePath(path)) return true;
      if (q) {
        // No matches on this tab — keep sections expanded so fields are not hidden.
        if (activeTabKey && pathTab === activeTabKey && !hasMatchesOnActiveTab) {
          return manualOpen[path] !== false;
        }
        if (dismissedWhileSearching.has(path)) return false;
        if (matchingPaths.has(path)) {
          if (tabsUrlParam && activeTabKey && pathTab !== activeTabKey) {
            return manualOpen[path] === true;
          }
          return true;
        }
        return manualOpen[path] === true;
      }
      // Default expanded; explicit collapse sets manualOpen[path] === false.
      return manualOpen[path] !== false;
    },
    [
      q,
      matchingPaths,
      hasMatchesOnActiveTab,
      dismissedWhileSearching,
      manualOpen,
      tabsUrlParam,
      activeTabKey,
      isCollapsiblePath,
    ],
  );

  const onSectionToggle = useCallback(
    (path: string, expanded: boolean) => {
      if (q) {
        setDismissedWhileSearching((prev) => {
          const next = new Set(prev);
          if (expanded) next.delete(path);
          else next.add(path);
          return next;
        });
        setManualOpen((prev) => ({ ...prev, [path]: expanded }));
        return;
      }
      setManualOpen((prev) => ({ ...prev, [path]: expanded }));
    },
    [q],
  );

  const focusSectionPath = useCallback(
    (path: string) => {
      const entry = searchIndex.find((e) => e.path === path);
      const tabKey = entry?.tabKey || path.replace(/^__tab__/, '').split('.')[0];

      if (tabsUrlParam && tabKey) {
        const next = new URLSearchParams(searchParams);
        next.set(tabsUrlParam, tabKey);
        setSearchParams(next, { replace: true });
      }

      if (!path.startsWith('__tab__')) {
        setManualOpen((prev) => ({ ...prev, [path]: true }));
      }

      const domId = sectionPathToDomId(path);
      const scrollTo = () => {
        document.getElementById(domId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      };
      if (tabsUrlParam && tabKey) {
        window.setTimeout(scrollTo, 120);
      } else {
        window.requestAnimationFrame(scrollTo);
      }
    },
    [searchIndex, searchParams, setSearchParams, tabsUrlParam],
  );

  const expandAllSections = useCallback(() => {
    setManualOpen((prev) => {
      const next = { ...prev };
      for (const e of searchIndex) {
        if (e.path.startsWith('__tab__') || !isCollapsiblePath(e.path)) continue;
        if (tabsUrlParam && activeTabKey && sectionPathRootTabKey(e.path) !== activeTabKey) {
          continue;
        }
        next[e.path] = true;
      }
      return next;
    });
    setSearchQuery('');
  }, [searchIndex, isCollapsiblePath, tabsUrlParam, activeTabKey]);

  const collapseAllSections = useCallback(() => {
    setManualOpen((prev) => {
      const next = { ...prev };
      for (const e of searchIndex) {
        if (e.path.startsWith('__tab__') || !isCollapsiblePath(e.path)) continue;
        if (tabsUrlParam && activeTabKey && sectionPathRootTabKey(e.path) !== activeTabKey) {
          continue;
        }
        next[e.path] = false;
      }
      return next;
    });
    setDismissedWhileSearching(new Set());
    setSearchQuery('');
  }, [searchIndex, isCollapsiblePath, tabsUrlParam, activeTabKey]);

  const value = useMemo(
    () => ({
      searchQuery,
      setSearchQuery,
      searchIndex,
      matchingPaths,
      isSectionExpanded,
      onSectionToggle,
      focusSectionPath,
      expandAllSections,
      collapseAllSections,
      tabsUrlParam,
      isCollapsiblePath,
    }),
    [
      searchQuery,
      searchIndex,
      matchingPaths,
      isSectionExpanded,
      onSectionToggle,
      focusSectionPath,
      expandAllSections,
      collapseAllSections,
      tabsUrlParam,
      isCollapsiblePath,
    ],
  );

  return (
    <SectionCollapseContext.Provider value={value}>{children}</SectionCollapseContext.Provider>
  );
}

export function ItemEditSectionSearchBar({ tabKey }: { tabKey: string }) {
  const ctx = useSectionCollapse();
  const theme = useTheme();
  if (!ctx) return null;

  const {
    searchQuery,
    setSearchQuery,
    searchIndex,
    focusSectionPath,
    expandAllSections,
    collapseAllSections,
  } = ctx;

  const q = searchQuery.trim().toLowerCase();
  const scoped = useMemo(() => {
    if (!q) return [];
    return searchIndex.filter(
      (e) => e.tabKey === tabKey && e.labels.some((l) => l.toLowerCase().includes(q)),
    );
  }, [q, searchIndex, tabKey]);

  return (
    <Box
      sx={{
        mb: 1.5,
        p: 1,
        borderRadius: 1,
        border: '1px dashed',
        borderColor: 'divider',
        bgcolor:
          theme.palette.mode === 'dark'
            ? 'rgba(255,255,255,0.04)'
            : theme.palette.grey[100],
      }}
    >
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75, letterSpacing: 0.02 }}>
        Filter this tab
      </Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
        <TextField
          size="small"
          fullWidth
          placeholder="Section or field label…"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          aria-label="Filter sections on this tab"
          sx={{
            '& .MuiInputBase-root': {
              fontSize: '0.8125rem',
              bgcolor: 'background.paper',
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18 }} color="action" />
              </InputAdornment>
            ),
            endAdornment: searchQuery ? (
              <InputAdornment position="end">
                <IconButton
                  type="button"
                  size="small"
                  aria-label="Clear filter"
                  edge="end"
                  onClick={() => setSearchQuery('')}
                >
                  <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </InputAdornment>
            ) : undefined,
          }}
        />
        <Stack direction="row" spacing={0.5} flexShrink={0}>
          <Button type="button" size="small" variant="text" color="inherit" sx={{ fontSize: '0.75rem' }} onClick={expandAllSections}>
            Expand all
          </Button>
          <Button type="button" size="small" variant="text" color="inherit" sx={{ fontSize: '0.75rem' }} onClick={collapseAllSections}>
            Collapse all
          </Button>
        </Stack>
      </Stack>
      {q && scoped.length > 0 && (
        <Box
          sx={{
            mt: 1,
            maxHeight: 180,
            overflow: 'auto',
            borderRadius: 0.5,
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
          }}
        >
          <List dense disablePadding>
            {scoped.map((e) => {
              const matchCount = countLabelsMatchingQuery(e.labels, q);
              const matchPhrase = matchCount === 1 ? '1 match' : `${matchCount} matches`;
              return (
                <ListItemButton
                  key={e.path}
                  dense
                  onClick={() => focusSectionPath(e.path)}
                  sx={{ py: 0.25 }}
                >
                  <ListItemText
                    primary={e.sectionTitle}
                    secondary={matchPhrase}
                    primaryTypographyProps={{ variant: 'body2', fontSize: '0.8125rem' }}
                    secondaryTypographyProps={{ variant: 'caption', fontSize: '0.7rem' }}
                  />
                </ListItemButton>
              );
            })}
          </List>
        </Box>
      )}
      {q && scoped.length === 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: 'block', fontSize: '0.7rem' }}>
          No matching labels on this tab.
        </Typography>
      )}
    </Box>
  );
}
